import React, { useEffect, useState } from "react";
import { clearSession } from "../shared/_helpers/session";
import { Link, NavLink, useNavigate, useParams, useLocation } from "react-router-dom";
import { confirmAppointmentRescheduleRequest, getClientAppointments, rateMeeting, getClientAppointmentsByParticularAdvisor, checkBlockedStatus, updateAppointmentStatus, getCompletedAppointmentsByClientId, getNotes, saveNotes } from "../shared/_services/common.services";
import { CLIENT_ACCESS_DENIED_MESSAGE, CLIENT_ACCESS_DENIED_REDIRECT, isForbiddenClientAccess } from "../shared/_helpers/apiErrors";
import moment from 'moment';
import { useSnackbar } from "notistack";
import Loader from "./Loader";
import DashboardUserHeader from "../components/dashboard-flow/DashboardUserHeader";
import { Editor } from 'primereact/editor';
import 'primereact/resources/primereact.min.css';
import StarsIcon from "../styles/StarsIcon";
import { Badge, Button, Dialog, DialogContent, DialogTitle, Page, PageHead, Panel, Textarea, cn } from "./ui";
import meetingPlaceholder from "../assets/images/No Meetings.png"


const PRIVILEGED_USER_TYPES = ["advisor", "super-admin", "admin"];
const CLIENT_NOTE_USER_TYPES = ["client", "super-admin", "admin"];

function isPrivilegedUserType(userType) {
  return PRIVILEGED_USER_TYPES.includes(userType);
}

function canOpenNotes(userType) {
  return CLIENT_NOTE_USER_TYPES.includes(userType);
}

function getAdvisorInitials(advisor) {
  const firstInitial = advisor?.firstName?.[0]?.toUpperCase() || "";
  const lastInitial = advisor?.lastName?.[0]?.toUpperCase() || "";
  return `${firstInitial}${lastInitial}`;
}

function getMeetingTimeRange(time, duration) {
  if (!time || !duration) return "";
  const start = moment(time, "h:mm A").format("h:mm A");
  const end = moment(time, "h:mm A").add(duration, "minutes").format("h:mm A");
  return `${start} - ${end}`;
}

function getMeetingDayLabel(date) {
  return moment.utc(date).format("dddd MMM Do");
}

function shouldShowMeetingDateHeader(currentDate, previousDate) {
  return currentDate !== previousDate;
}

const STATUS_TONES = { completed: "success", upcoming: "accent", pending: "warning" };

function MeetingRatingSection({
  meeting,
  userType,
  isReadMore,
  onToggleReadMore,
  onRateClick,
}) {
  if (meeting?.appointment_status !== "completed") {
    return <div className="tw-min-w-0 max-rail:tw-hidden rail:tw-flex-1"></div>;
  }

  const isPrivileged = isPrivilegedUserType(userType);
  const ratingComment = meeting?.rating_comment || "";
  const hasLongComment = ratingComment.length > 30;

  return (
    <div className="tw-min-w-0 rail:tw-flex-1">
      <div>
        <div data-slot="ratings-stars" className="tw-flex tw-gap-px tw-text-text-accent">
          {[1, 2, 3, 4, 5].map((star) => (
            <StarsIcon
              key={star}
              name={star <= (meeting?.rating || 0) ? "starFill" : "star"}
              className="tw-h-4 tw-w-4 tw-shrink-0"
            />
          ))}
        </div>
        {!isPrivileged && (
          <Link
            onClick={() => onRateClick(meeting)}
            className="tw-cursor-pointer tw-text-xs tw-font-semibold tw-text-link tw-no-underline hover:tw-text-link-hover"
          >
            Rate this meeting
          </Link>
        )}
        {isPrivileged && (
          <p className="tw-mb-0 tw-mt-2 tw-text-sm tw-text-text-muted">
            {hasLongComment ? (
              <>
                <span>{isReadMore ? ratingComment : `${ratingComment.slice(0, 15)}...`}</span>
                <span
                  onClick={onToggleReadMore}
                  className="tw-cursor-pointer tw-pl-1 tw-font-semibold tw-text-link"
                >
                  {isReadMore ? "Read Less" : "Read More"}
                </span>
              </>
            ) : (
              <span>{ratingComment}</span>
            )}
          </p>
        )}
      </div>
    </div>
  );
}

function MeetingPrimaryAction({ meeting, onJoinMeeting }) {
  if (meeting?.appointment_status === "completed") {
    return (
      <Button
        as={NavLink}
        size="sm"
        variant="ghost"
        to={{ pathname: "/meeting-details" }}
        state={{
          id: meeting._id,
          date: meeting.date,
          time: meeting.time,
          duration: meeting.duration,
          userId: meeting.clientId,
        }}
        data-slot="meeting-btn"
      >
        Details
      </Button>
    );
  }

  return (
    <Button
      size="sm"
      data-slot="meeting-btn"
      onClick={() => onJoinMeeting(meeting)}
    >
      <StarsIcon name="video" className="tw-h-4 tw-w-4 tw-shrink-0" />
      Join
    </Button>
  );
}

function MeetingRescheduleRequest({
  meeting,
  onConfirmReschedule,
}) {
  if (!(meeting?.reschedule_request && meeting.appointment_status === "pending")) {
    return null;
  }

  return (
    <div
      data-slot="reschedule-req-box"
      className="tw-mt-4 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-rounded-md tw-border tw-border-solid tw-border-divider tw-bg-overlay-warning-600/10 tw-p-4"
    >
      <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-2 max-rail:tw-basis-full">
        <StarsIcon name="alert" className="tw-h-5 tw-w-5 tw-shrink-0 tw-text-warning" />
        <p className="tw-m-0 tw-text-sm tw-text-text">
          Your financial Advisor has requested to change the meeting
          to <b>{getMeetingDayLabel(meeting?.date)}</b> at <b>{moment.utc(meeting.time, "HH:mm").format("h:mm A")} - {moment.utc(meeting.time, "HH:mm").add(30, "minute").format("h:mm A")}</b>{" "}
        </p>
      </div>
      <div className="tw-flex tw-gap-2">
        <Button size="sm" variant="quiet" onClick={() => onConfirmReschedule(meeting?._id, 0)}>
          <StarsIcon name="x" className="tw-h-4 tw-w-4 tw-shrink-0" />
          Deny
        </Button>
        <Button size="sm" onClick={() => onConfirmReschedule(meeting?._id, 1)}>
          <StarsIcon name="check" className="tw-h-4 tw-w-4 tw-shrink-0" />
          Accept
        </Button>
      </div>
    </div>
  );
}

function MeetingCard({
  meeting,
  previousMeetingDate,
  userType,
  openingNotesId,
  isReadMore,
  onToggleReadMore,
  onOpenEditorWithDelay,
  onJoinMeeting,
  onRateClick,
  onConfirmReschedule,
}) {
  const currentMeetingDate = moment(meeting?.date).format("YYYY-MM-DD");
  const showDateHeader = shouldShowMeetingDateHeader(currentMeetingDate, previousMeetingDate);
  const advisorHasImage = Boolean(meeting?.advisor?.profile_image);

  return (
    <div data-slot="meeting-single" className="tw-mb-4">
      {showDateHeader && <h6 className="tw-mb-2 tw-mt-0 tw-text-xs tw-font-bold tw-text-text-muted">{getMeetingDayLabel(meeting?.date)}</h6>}
      <Panel as="div" data-slot="meeting-dtl-box" className="tw-p-4">
        <div className="tw-flex tw-flex-col tw-gap-4 rail:tw-flex-row rail:tw-items-center">
          <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-3 rail:tw-flex-1">
            <div data-slot="profile-pic" className="tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-overflow-hidden tw-rounded-pill tw-bg-accent-tint tw-text-sm tw-font-bold tw-text-text-accent">
              {advisorHasImage ? (
                <img src={meeting?.advisor?.profile_image} alt="" className="tw-h-full tw-w-full tw-object-cover" />
              ) : (
                <h3 className="tw-m-0 tw-text-sm tw-font-bold tw-text-inherit">{getAdvisorInitials(meeting?.advisor)}</h3>
              )}
            </div>
            <div className="tw-min-w-0">
              <p data-slot="meeting-time" className="tw-m-0 tw-text-sm tw-font-bold tw-tabular-nums tw-text-text">{getMeetingTimeRange(meeting?.time, meeting?.duration)}</p>
              <p data-slot="meeting-status" data-status={meeting?.appointment_status} className="tw-m-0">
                {meeting?.reschedule_request && (
                  <Badge className="tw-capitalize" tone={STATUS_TONES[meeting?.appointment_status] || "neutral"}>{meeting?.appointment_status}</Badge>
                )}
              </p>
            </div>
          </div>
          <div className="tw-min-w-0 rail:tw-flex-1">
            <p data-slot="advisor-name" className="tw-m-0 tw-text-sm tw-font-bold tw-text-text">{meeting?.advisor?.firstName} {meeting?.advisor?.lastName}</p>
            <p className="tw-m-0 tw-text-xs tw-text-text-muted">Financial Advisor</p>
          </div>

          <MeetingRatingSection
            meeting={meeting}
            userType={userType}
            isReadMore={isReadMore}
            onToggleReadMore={onToggleReadMore}
            onRateClick={onRateClick}
          />

          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 rail:tw-justify-end">
            {canOpenNotes(userType) && (
              <Button
                size="sm"
                variant="quiet"
                data-slot="meeting-btn"
                onClick={() => onOpenEditorWithDelay(meeting._id)}
                disabled={openingNotesId === meeting._id}
              >
                <StarsIcon name="edit" className="tw-h-4 tw-w-4 tw-shrink-0" />
                {openingNotesId === meeting._id ? "Loading..." : "Notes"}
              </Button>
            )}

            <MeetingPrimaryAction
              meeting={meeting}
              onJoinMeeting={onJoinMeeting}
            />
          </div>
        </div>
        <MeetingRescheduleRequest
          meeting={meeting}
          onConfirmReschedule={onConfirmReschedule}
        />
      </Panel>
    </div>
  );
}

const MeetingList = () => {
  const [reviewPopupShow, setReviewPopupShow] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const params = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const pathname = queryParams.get('isadmin');
  const [showLoader, setShowLoader] = useState(false);
  const navigate = useNavigate();
  const [rating, setSelectedRating] = useState(0);
  const [comment, setComment] = useState('');
  const [appointmentId, setAppointmentId] = useState('');
  const [isReadMore, setIsReadMore] = useState(false);
  const [editorPopupShow, setEditorPopupShow] = useState(false);
  const [activeNoteId, setActiveNoteId] = useState(null);
  const [notes, setNotes] = useState({});
  
  const [openingNotesId, setOpeningNotesId] = useState(null);


  const userType = JSON.parse(localStorage.getItem('currentUser'))?.user?.userType;

  const getAppointments = async () => {
    setShowLoader(true)
    let data;
    if (params?.clientId && pathname) {
      data = await getCompletedAppointmentsByClientId(params?.clientId);
    } else if (params?.clientId) {
      data = await getClientAppointmentsByParticularAdvisor({
        clientId: params?.clientId,
        advisorId: JSON.parse(localStorage.getItem('currentUser'))?.user?._id,
      });
    } else {
      data = await getClientAppointments(
        JSON.parse(localStorage.getItem('currentUser'))?.user?._id
      );
    }
    if (data?.status === 200) {
      if (data?.data?.length > 0) {
        setAppointments([...data.data])
      }
    }
    else {
      if (data?.response?.status === 401) {
        clearSession();
        enqueueSnackbar("Your session has been expired!", {
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
        navigate('/login');
      } else if (isForbiddenClientAccess(data)) {
        enqueueSnackbar(CLIENT_ACCESS_DENIED_MESSAGE, {
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
        navigate(CLIENT_ACCESS_DENIED_REDIRECT);
      }
    }
    setShowLoader(false)
  }
  const checkBlocked = async () => {
    const userId = JSON.parse(localStorage.getItem('currentUser')).user?._id;
    try {
      // Call the API to check the user's blocked status
      const response = await checkBlockedStatus(userId);
      if (response.data ? response.data.status : response.response.status === 401) {
        clearSession();
        navigate("/login");
      }
      else {
        const { isBlocked, isDeleted } = response.data;
        if (isBlocked || isDeleted) {
          clearSession();
          navigate("/login");
        }
      }
    } catch (error) {
      console.error("Error checking blocked status:", error);
    }
  }
  
  
  // Function to check and update past appointments
  const checkAndUpdateAppointments = async () => {
  const pastAppointments = Array.isArray(appointments)
    ? appointments.filter((appt) => {
        if (!appt?.date || !appt?.time || !appt?.timezone || !appt?.duration) {
          return false;
        }
        // Extract date portion from ISO string
        const localDate = appt.date.split("T")[0]; // e.g., "2025-10-15"
       // Parse start time in the correct timezone
        const startTime = moment.tz(
          `${localDate} ${appt.time}`,
          "YYYY-MM-DD h:mm A",
          appt.timezone
        );
        // Calculate end time by adding duration
       const endTime = startTime.clone().add(appt.duration, "minutes");
        // Check if current time is after end time
        const hasEnded = moment().isAfter(endTime);
        // Only consider appointments that are still marked as "upcoming"
        const isUpcoming = appt.appointment_status === "upcoming";
  
        return hasEnded && isUpcoming;
      })
    : [];

  if (pastAppointments.length > 0) {
    for (const appointment of pastAppointments) {
      try {
        const res = await updateAppointmentStatus(appointment._id, "completed");
        if (res.status === 200) {
          await getAppointments();
        }
      } catch (error) {
      }
    }
  }
};

  useEffect(() => {
    checkBlocked();
    getAppointments();
    checkAndUpdateAppointments();
  }, []); //eslint-disable-line

  const handleJoinMeeting = async (data) => {
    window.open(data.join_url, '_blank');
  };

  const handleConfirmReschedule = async (id, status) => {
    setShowLoader(true)
    let data = await confirmAppointmentRescheduleRequest({ appointmentId: id, status });

    if (data.status === 200) {
      const res = await getClientAppointments(JSON.parse(localStorage.getItem('currentUser'))?.user?._id);
      if (res) {
        setAppointments(res.data);
        setShowLoader(false)
      }
      enqueueSnackbar(data?.data?.message, {
        variant: 'success',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
    }
  }


  const handleRateClick = (data) => {
    setReviewPopupShow(true)
    setAppointmentId(data?._id);
    setSelectedRating(data?.rating);
    setComment(data?.rating_comment);
  }

  const handleStarClick = (index) => {
    setSelectedRating(index + 1);
  };

  const handleSubmit = async () => {
    if (rating === 0 && !comment.trim()) {
      return enqueueSnackbar('Please select a rating and add a comment.', { variant: 'warning' });
    }
    const response = await rateMeeting({
      appointmentId,
      rating,
      comment,
    });

    if (response) {
      enqueueSnackbar('Rating submitted successfully.', { variant: 'success' });
      setReviewPopupShow(false);
      getAppointments();
    } else {
      enqueueSnackbar('Failed to submit rating. Please try again.', { variant: 'error' });
    }
  };

const handleOpenEditorWithDelay = (id) => {
  setOpeningNotesId(id);
  setTimeout(() => {
    handleOpenEditor(id); 
  }, 300); 
};

const handleOpenEditor = async (appointmentId) => {
  try {
    const response = await getNotes(appointmentId, "client");
    if (response.status === 200) {
      setNotes({ ...notes, [appointmentId]: response.data.data.notes || '' });
      setActiveNoteId(appointmentId);
      setEditorPopupShow(true);
    } else {
      enqueueSnackbar(response.data?.message || "Failed to retrieve notes.", { variant: 'error' });
    }
  } catch (error) {
    console.error("Error retrieving notes:", error);
    enqueueSnackbar("Error retrieving notes.", { variant: 'error' });
  } finally {
    setOpeningNotesId(null); // Clear the loading state
  }
};

  const handleSaveNotes = async (appointmentId) => {
    try {
      const response = await saveNotes(appointmentId, notes[appointmentId], "client");
      if (response.status === 200) {
        enqueueSnackbar("Notes saved!", { variant: 'success' });
        setEditorPopupShow(false);
      } else {
        enqueueSnackbar(response.data?.message || "Failed to save notes.", { variant: 'error' });
      }
    } catch (error) {
      console.error("Error saving notes:", error);
      enqueueSnackbar("Error saving notes.", { variant: 'error' });
    }
  };

  checkAndUpdateAppointments();

  const toggleReadMore = () => {
    setIsReadMore(!isReadMore);
  };

  const [selectedFilter, setSelectedFilter] = useState("All");

  // Filter logic
  const filteredAppointments = appointments?.filter((appt) => {
    if (selectedFilter === "All") return true;
    if (selectedFilter === "Upcoming") return appt.appointment_status === "upcoming";
    if (selectedFilter === "Pending") return appt.appointment_status === "pending";
    if (selectedFilter === "Completed") return appt.appointment_status === "completed";
    return true;
  });

  return (
    <>
      <Page>
      {(JSON.parse(localStorage.getItem("currentUser"))?.user?.userType === 'advisor' ||
        JSON.parse(localStorage.getItem("currentUser"))?.user?.userType === 'super-admin' ||
        JSON.parse(localStorage.getItem("currentUser"))?.user?.userType === 'admin') &&
        <DashboardUserHeader clientId={params?.clientId ? params?.clientId : null} />}

      {JSON.parse(localStorage.getItem("currentUser"))?.user?.userType === 'client' &&
        <PageHead
          title="Meetings"
          subtitle="Your complete log of past, present, and upcoming meetings."
        />}

      {showLoader ? <Loader /> :
        <div data-slot="meetings-body">
          <div
            data-slot="user-meetings-filter"
            className="tw-mb-6 tw-inline-flex tw-gap-1 tw-rounded-pill tw-border tw-border-solid tw-border-divider tw-bg-surface-sunken tw-p-1"
          >
            {["All", "Upcoming", "Pending","Completed"].map((filter) => (
              <button
                type="button"
                key={filter}
                aria-pressed={selectedFilter === filter}
                data-slot="user-meetings-filter-single"
                data-selected={selectedFilter === filter}
                className={cn(
                  "tw-min-h-9 tw-cursor-pointer tw-rounded-pill tw-border-0 tw-px-4 tw-text-xs tw-font-bold max-rail:tw-min-h-11",
                  selectedFilter === filter
                    ? "tw-bg-action tw-text-action-text"
                    : "tw-bg-transparent tw-text-text-muted",
                )}
                onClick={() => setSelectedFilter(filter)}
              >
                {filter}
              </button>
            ))}
          </div>
          {filteredAppointments?.length > 0 ? filteredAppointments?.map((data, index) => {
            const previousMeetingDate = index > 0 ? moment(filteredAppointments[index - 1]?.date).format("YYYY-MM-DD") : null;
            return (
              <MeetingCard
                key={data?._id}
                meeting={data}
                previousMeetingDate={previousMeetingDate}
                userType={userType}
                openingNotesId={openingNotesId}
                isReadMore={isReadMore}
                onToggleReadMore={toggleReadMore}
                onOpenEditorWithDelay={handleOpenEditorWithDelay}
                onJoinMeeting={handleJoinMeeting}
                onRateClick={handleRateClick}
                onConfirmReschedule={handleConfirmReschedule}
              />
            );
          }) : (
            <>
            <div className="tw-mx-auto tw-max-w-lg tw-rounded-lg tw-bg-overlay-white tw-p-6 tw-text-center">
              <img src={meetingPlaceholder} alt="no meeting" className="tw-inline-block tw-max-w-full"/>
            </div>
            </>
          )}
        </div>}
      </Page>

      {/* thank you popup */}
      <Dialog
        open={reviewPopupShow}
        onOpenChange={(o) => !o && setReviewPopupShow(false)}
      >
        <DialogContent
          data-slot="reviewPopup"
          aria-describedby={undefined}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <form>
            <div className="tw-flex tw-justify-end">
              <Button
                variant="icon"
                size="sm"
                iconOnly
                aria-label="Close"
                data-slot="close-popup-btn"
                onClick={() => setReviewPopupShow(false)}
              >
                <StarsIcon name="x" className="tw-h-4 tw-w-4 tw-shrink-0" />
              </Button>
            </div>
            <div data-slot="ratings-stars" className="tw-mb-4 tw-flex tw-justify-center tw-gap-1 tw-text-text-accent">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  type="button"
                  key={star}
                  aria-label={`${star} star${star > 1 ? "s" : ""}`}
                  aria-pressed={star <= rating}
                  className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-1 tw-text-text-accent"
                  onClick={() => handleStarClick(star - 1)}
                >
                  <StarsIcon name={star <= rating ? "starFill" : "star"} className="tw-h-6 tw-w-6 tw-shrink-0" />
                </button>
              ))}
            </div>
            <DialogTitle className="tw-text-center">Thank you for you rating.</DialogTitle>
            <p className="tw-mb-6 tw-mt-0 tw-text-center tw-text-sm tw-text-text-muted">Let us know how we can improve!</p>
            <Textarea
              placeholder="Share your thoughts here."
              id="review"
              name="review"
              rows="5"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <div className="tw-mt-6 tw-text-center">
              <Button onClick={handleSubmit}>
                <StarsIcon name="check" className="tw-h-5 tw-w-5 tw-shrink-0" />
                Submit Review
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Editor Popup */}
      <Dialog
        open={editorPopupShow}
        onOpenChange={(o) => !o && setEditorPopupShow(false)}
      >
        <DialogContent
          data-slot="editorPopup"
          className="tw-max-w-3xl"
          aria-describedby={undefined}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <div className="tw-flex tw-justify-end">
            <Button
              variant="icon"
              size="sm"
              iconOnly
              aria-label="Close"
              data-slot="close-popup-btn"
              onClick={() => setEditorPopupShow(false)}
            >
              <StarsIcon name="x" className="tw-h-4 tw-w-4 tw-shrink-0" />
            </Button>
          </div>
          <DialogTitle className="tw-text-center">Meeting Notes</DialogTitle>
          <Editor
            value={notes[activeNoteId] || ''}
            onTextChange={(e) =>
              setNotes({ ...notes, [activeNoteId]: e.htmlValue })
            }
            style={{ height: '300px' }}
          />
          <div className="tw-mt-6 tw-text-center">
            <Button onClick={() => handleSaveNotes(activeNoteId)}>
              <StarsIcon name="save" className="tw-h-4 tw-w-4 tw-shrink-0" />
              Save Notes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MeetingList;
